import React, { useEffect, useMemo, useState } from 'react';
import { IS_VATAR } from '../vatar/env';
import { postScore } from '../vatar/vatarClient';
import { useNavigate } from 'react-router-dom';
import useSoloStore, { BOT_CONFIGS } from '../stores/soloStore';
import useCoinStore from '../stores/coinStore';
import { BotMascot, MascotAvatar, useMyMascot } from '../Components/Mascot';
import analytics from '../Services/analyticsService';
import ShareResultCard from '../Components/ShareResultCard';
import { soloCard, soloCardCopy } from '../share/resultCards';

export default function SoloFinalResults() {
  const navigate = useNavigate();
  const {
    userScore,
    botScore,
    selectedBot,
    gameMode,
    roundHistory,
    endGame,
    startGame,
    enableSpecialRounds,
    specialRounds,
    resetGame,
  } = useSoloStore();

  const { addCoins } = useCoinStore();
  const botConfig = BOT_CONFIGS[selectedBot] || {};
  const myMascot = useMyMascot();

  const result =
    userScore > botScore ? 'win' : userScore < botScore ? 'loss' : 'tie';

  const card = useMemo(
    () => soloCard({ userScore, botScore, bot: selectedBot, history: roundHistory, name: localStorage.getItem('playerName') || 'Me', mode: gameMode }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const cardArt = useMemo(
    () => ({
      me: { mascot: myMascot, mood: result === 'win' ? 'celebrate' : result === 'loss' ? 'sad' : 'happy', letter: card.me[0] },
      bot: { bot: selectedBot, state: result === 'win' ? 'lost' : result === 'loss' ? 'won' : 'idle' },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const coinReward =
    result === 'win' && gameMode === 'full'
      ? botConfig.rewardOnWin || 0
      : result === 'tie' && gameMode === 'full'
      ? botConfig.rewardOnTie || 0
      : 0;

  const [coinsAwarded, setCoinsAwarded] = useState(false);

  useEffect(() => {
    if (IS_VATAR) postScore(userScore);
    endGame(result);

    if (coinReward > 0) {
      addCoins(coinReward, {
        source: 'solo_win',
        bot: selectedBot,
        gameMode,
      }).then(() => setCoinsAwarded(true));
    }
  }, []);


  const resultConfig = {
    win: {
      emoji: '🏆',
      title: 'You Won!',
      subtitle: `You beat ${botConfig.name}!`,
      bg: 'bg-green-50',
      border: 'border-green-200',
      titleColor: 'text-green-700',
    },
    loss: {
      emoji: '😤',
      title: 'You Lost.',
      subtitle: `${botConfig.name} beat you this time.`,
      bg: 'bg-red-50',
      border: 'border-red-200',
      titleColor: 'text-red-700',
    },
    tie: {
      emoji: '🤝',
      title: "It's a Tie!",
      subtitle: `You matched ${botConfig.name}.`,
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      titleColor: 'text-yellow-700',
    },
  }[result];

  function handlePlayAgain() {
    startGame(selectedBot, gameMode, enableSpecialRounds, specialRounds);
    navigate('/solo/game');
  }

  function handleHome() {
    resetGame();
    navigate('/');
  }

  function handleRunWarmup() {
    analytics.track('run_entry_tapped', { source: 'solo_results' });
    navigate('/run', { state: { from: 'solo_results' } });
  }

  return (
    <div className="min-h-screen bg-orange-50 flex flex-col pb-8">
      <div className="flex flex-col w-full lg:max-w-5xl lg:mx-auto lg:grid lg:grid-cols-2 lg:gap-x-6 lg:items-start lg:px-6 lg:pt-8">
      <div className="contents lg:block">
      {/* Result banner */}
      <div
        className={`mx-6 mt-8 lg:mx-0 lg:mt-0 ${resultConfig.bg} border ${resultConfig.border} rounded-3xl px-6 py-8 flex flex-col items-center shadow-sm`}
      >
        <span className="text-6xl mb-2">{resultConfig.emoji}</span>
        <h1 className={`text-3xl font-black ${resultConfig.titleColor}`}>
          {resultConfig.title}
        </h1>
        <p className="text-gray-500 mt-1">{resultConfig.subtitle}</p>
        {!IS_VATAR && (
          <div className="mt-4">
            <ShareResultCard
              compact
              type="solo"
              card={card}
              getCopy={() => soloCardCopy(card)}
              art={cardArt}
              label={`Solo vs ${botConfig.name}`}
            />
          </div>
        )}
      </div>

      {/* Coin reward */}
      {coinReward > 0 && (
        <div className="mx-6 mt-4 lg:mx-0 bg-yellow-400 rounded-2xl px-4 py-3 flex items-center justify-between shadow">
          <div>
            <p className="font-bold text-yellow-900">Coin Reward</p>
            <p className="text-sm text-yellow-800">
              {result === 'win' ? 'Beat Kemi in Full Game' : 'Tied Kemi in Full Game'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-black text-yellow-900">+{coinReward}</p>
            {coinsAwarded && (
              <p className="text-xs text-yellow-700">Added to balance ✓</p>
            )}
          </div>
        </div>
      )}

      {/* Score comparison */}
      <div className="mx-6 mt-4 lg:mx-0 bg-white rounded-2xl shadow-sm px-4 py-4 flex justify-around items-center lg:py-6">
        <div className="text-center flex flex-col items-center">
          <div className="h-14 flex items-end">
            <MascotAvatar
              mascot={myMascot}
              mood={result === 'win' ? 'celebrate' : result === 'loss' ? 'sad' : 'happy'}
              size={myMascot.base === 'pencil' ? 37 : 56}
              shadow={false}
            />
          </div>
          <p className="text-xs text-gray-400 mb-1">You</p>
          <p className="text-4xl font-black text-red-600">{userScore}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-300">vs</p>
        </div>
        <div className="text-center flex flex-col items-center">
          <BotMascot bot={selectedBot} state={result === 'loss' ? 'won' : result === 'win' ? 'lost' : 'idle'} size={56} />
          <p className="text-xs text-gray-400 mb-1">{botConfig.name}</p>
          <p
            className="text-4xl font-black"
            style={{ color: botConfig.color || '#6b7280' }}
          >
            {botScore}
          </p>
        </div>
      </div>


      </div>
      <div className="contents lg:block">
      {/* Full round history */}
      {roundHistory.length > 0 && (
        <div className="mx-6 mt-4 lg:mx-0 lg:mt-0 bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-bold text-gray-700">All Rounds</p>
          </div>
          {/* Table header */}
          <div className="flex px-4 py-2 bg-gray-50 border-b border-gray-100">
            <div className="w-14 text-xs font-semibold text-gray-400">Round</div>
            <div className="w-10 text-xs font-semibold text-gray-400">Letter</div>
            <div className="flex-1 text-xs font-semibold text-gray-400 text-center">You</div>
            <div className="flex-1 text-xs font-semibold text-gray-400 text-center">{botConfig.name}</div>
          </div>
          {roundHistory.map((round) => {
            const userWon = round.userTotal > round.botTotal;
            const botWon  = round.botTotal  > round.userTotal;
            return (
              <div
                key={round.round}
                className="flex items-center px-4 py-3 border-b border-gray-50 last:border-0"
              >
                <div className="w-14 text-xs text-gray-400 font-medium">{round.round}</div>
                <div className="w-10 text-sm font-black text-red-600">{round.letter}</div>
                <div className="flex-1 text-center">
                  <span className={`text-sm font-bold ${userWon ? 'text-emerald-600' : 'text-gray-400'}`}>
                    +{round.userTotal}
                  </span>
                </div>
                <div className="flex-1 text-center">
                  <span className={`text-sm font-bold ${botWon ? 'text-emerald-600' : 'text-gray-400'}`}>
                    +{round.botTotal}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Buttons */}
      <div className="px-6 py-6 space-y-3 lg:px-0">
        <button
          onClick={handlePlayAgain}
          className="w-full py-4 rounded-2xl font-bold text-lg text-white bg-red-600 hover:bg-red-700 shadow-lg active:scale-95 transition-all"
        >
          Play Again vs {botConfig.name}
        </button>
        <button
          onClick={handleHome}
          className="w-full py-3 rounded-2xl font-semibold text-gray-600 bg-white shadow-sm hover:bg-gray-50 transition-all"
        >
          Back to Home
        </button>
        <button
          type="button"
          onClick={handleRunWarmup}
          className="w-full py-2 text-sm font-semibold text-red-700 hover:text-red-800"
        >
          Warm up with NameIt Run →
        </button>
      </div>
      </div>
      </div>
    </div>
  );
}
